import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { api, FalhaApi, type ClienteApi, type VistoriaResumoApi } from "../api";
import { Layout } from "../componentes/Layout";
import { IconeCirculoCheck } from "../componentes/Icones";
import { useSessao } from "../estado/sessao";
import { fimDoMes, inicioDoMes } from "../utilCalendario";

function paraDataInput(data: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${data.getFullYear()}-${pad(data.getMonth() + 1)}-${pad(data.getDate())}`;
}

/**
 * Lista de clientes — cada um leva pra sua própria página com responsáveis (ver
 * ClienteDetalhe.tsx). Cliente = o prédio a vistoriar (fusão Cliente+Predio, 10/08): o desenho
 * anterior de 2 níveis (Cliente administradora → N Prédios) forçava cadastro duplo no caso comum,
 * já que quase todo cliente real é um prédio específico. Formulário de criação é inline (não
 * modal): overlay é uma péssima experiência no mobile, cobrindo a tela inteira e competindo com o
 * teclado virtual.
 */
export function Clientes() {
  const { usuario } = useSessao();
  const admin = usuario?.papel === "ADMIN";
  const [clientes, setClientes] = useState<ClienteApi[] | null>(null);
  const [mostrarForm, setMostrarForm] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [aba, setAba] = useState<"predios" | "cobertura">("predios");

  function carregar() {
    api
      .listarClientes()
      .then(({ clientes }) => setClientes(clientes))
      .catch(() => setErro("Falha ao carregar clientes"));
  }

  useEffect(carregar, []);

  async function excluir(id: string) {
    await api.excluirCliente(id);
    carregar();
  }

  return (
    <Layout>
      <main className="container">
        <h2>Prédios</h2>
        {erro && (
          <div className="alerta alerta-erro" role="alert">
            {erro}
          </div>
        )}

        {admin && (
          <div className="controle-segmentado" style={{ marginBottom: 12 }} aria-label="Seção de prédios">
            <button className={aba === "predios" ? "ativo" : ""} onClick={() => setAba("predios")}>Prédios</button>
            <button className={aba === "cobertura" ? "ativo" : ""} onClick={() => setAba("cobertura")}>Vistorias no período</button>
          </div>
        )}

        {aba === "cobertura" ? (
          <CoberturaPeriodo clientes={clientes ?? []} />
        ) : (
          <>
            {admin && (mostrarForm ? (
              <div className="cartao" style={{ padding: 16 }}>
                <FormularioCliente aoFechar={() => setMostrarForm(false)} aoCriar={carregar} />
              </div>
            ) : (
              <button className="botao botao-primario" onClick={() => setMostrarForm(true)}>
                + Novo cliente
              </button>
            ))}

            {clientes === null ? (
              <p style={{ marginTop: 16 }}>Carregando…</p>
            ) : clientes.length === 0 ? (
              <p style={{ marginTop: 16, color: "var(--texto-suave)" }}>Nenhum cliente cadastrado ainda.</p>
            ) : (
              <div className="cartao" style={{ padding: 0, marginTop: 16 }}>
                {clientes.map((cliente) => (
                  <div
                    key={cliente.id}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 8,
                      borderBottom: "1px solid var(--borda)",
                    }}
                  >
                    <Link to={`/clientes/${cliente.id}`} className="item-lista" style={{ flex: 1, minWidth: 0 }}>
                      <div className="item-lista-eyebrow">🏢 CLIENTE</div>
                      <div className="item-lista-titulo">{cliente.nome}</div>
                      <div className="item-lista-meta">
                        {[
                          cliente.administradora,
                          cliente.cidade ? `${cliente.cidade}${cliente.estado ? "/" + cliente.estado : ""}` : null,
                          cliente.cnpj,
                        ]
                          .filter(Boolean)
                          .join(" · ") || "Sem dados adicionais"}
                      </div>
                    </Link>
                    {admin && <button className="botao botao-texto" style={{ marginRight: 16 }} onClick={() => void excluir(cliente.id)}>Excluir</button>}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </main>
    </Layout>
  );
}

/** Checklist de cobertura: quais prédios já tiveram vistoria realizada (concluída, não só
 * iniciada) dentro do período escolhido. Padrão = mês corrente, mas o admin pode mudar pra
 * conferir qualquer intervalo (ex.: fechamento do mês anterior). */
function CoberturaPeriodo({ clientes }: { clientes: ClienteApi[] }) {
  const hoje = new Date();
  const [inicio, setInicio] = useState(() => paraDataInput(inicioDoMes(hoje)));
  const [fim, setFim] = useState(() => paraDataInput(fimDoMes(hoje)));
  const [vistorias, setVistorias] = useState<VistoriaResumoApi[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    api.listarVistorias().then(({ vistorias }) => setVistorias(vistorias)).catch(() => setErro("Falha ao carregar vistorias"));
  }, []);

  // dataVistoria vem como meia-noite UTC representando só a data (sem hora relevante) — os 10
  // primeiros caracteres do ISO já são o "AAAA-MM-DD" certo, mesma convenção usada na exibição em
  // Vistorias.tsx. Comparação de string funciona porque o formato é sempre AAAA-MM-DD.
  const clientesVistoriados = new Set(
    (vistorias ?? [])
      .filter((v) => v.status !== "EM_ANDAMENTO")
      .filter((v) => { const d = v.dataVistoria.slice(0, 10); return d >= inicio && d <= fim; })
      .map((v) => v.cliente.id)
  );
  const total = clientes.length;
  const vistoriadosCount = clientes.filter((c) => clientesVistoriados.has(c.id)).length;

  return (
    <div>
      <div className="cartao" style={{ padding: 16, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
        <div>
          <label htmlFor="cob-inicio">De</label>
          <input id="cob-inicio" type="date" value={inicio} onChange={(e) => setInicio(e.target.value)} />
        </div>
        <div>
          <label htmlFor="cob-fim">Até</label>
          <input id="cob-fim" type="date" value={fim} onChange={(e) => setFim(e.target.value)} />
        </div>
      </div>

      {erro && (
        <div className="alerta alerta-erro" role="alert" style={{ marginTop: 12 }}>
          {erro}
        </div>
      )}

      {vistorias === null ? (
        <p style={{ marginTop: 16 }}>Carregando…</p>
      ) : clientes.length === 0 ? (
        <p style={{ marginTop: 16, color: "var(--texto-suave)" }}>Nenhum cliente cadastrado ainda.</p>
      ) : (
        <>
          <p style={{ marginTop: 16, color: "var(--texto-suave)", fontSize: 14 }}>
            {vistoriadosCount} de {total} prédios com vistoria realizada no período.
          </p>
          <div className="cartao" style={{ padding: 0, marginTop: 8 }}>
            {clientes.map((cliente) => {
              const vistoriado = clientesVistoriados.has(cliente.id);
              return (
                <div
                  key={cliente.id}
                  style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", borderBottom: "1px solid var(--borda)" }}
                >
                  <IconeCirculoCheck tamanho={20} cor={vistoriado ? "var(--pill-verde-texto)" : "var(--borda)"} selecionado={vistoriado} />
                  <Link to={`/clientes/${cliente.id}`} style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600 }}>{cliente.nome}</div>
                    {cliente.administradora && <div style={{ fontSize: 13, color: "var(--texto-suave)" }}>{cliente.administradora}</div>}
                  </Link>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function FormularioCliente({ aoFechar, aoCriar }: { aoFechar: () => void; aoCriar: () => void }) {
  const [nome, setNome] = useState("");
  const [administradora, setAdministradora] = useState("");
  const [cnpj, setCnpj] = useState("");
  const [emailContato, setEmailContato] = useState("");
  const [telefoneContato, setTelefoneContato] = useState("");
  const [endereco, setEndereco] = useState("");
  const [cidade, setCidade] = useState("");
  const [estado, setEstado] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function aoEnviar(evento: FormEvent) {
    evento.preventDefault();
    setErro(null);
    setSalvando(true);
    try {
      await api.criarCliente({
        nome,
        administradora,
        cnpj,
        emailContato,
        telefoneContato,
        endereco,
        cidade,
        estado,
      });
      aoCriar();
      aoFechar();
    } catch (e) {
      setErro(e instanceof FalhaApi ? e.message : "Falha ao criar cliente");
    } finally {
      setSalvando(false);
    }
  }

  return (
    <form onSubmit={aoEnviar}>
      <h3 style={{ marginTop: 0 }}>Novo cliente</h3>
      {erro && (
        <div className="alerta alerta-erro" role="alert">
          {erro}
        </div>
      )}

      <label htmlFor="c-nome">Nome (ex: nome do prédio/condomínio)</label>
      <input id="c-nome" value={nome} onChange={(e) => setNome(e.target.value)} required autoFocus />

      <label htmlFor="c-administradora">Administradora (só se houver uma gestora terceira)</label>
      <input id="c-administradora" value={administradora} onChange={(e) => setAdministradora(e.target.value)} />

      <label htmlFor="c-endereco">Endereço</label>
      <input id="c-endereco" value={endereco} onChange={(e) => setEndereco(e.target.value)} />

      <div style={{ display: "flex", gap: 12 }}>
        <div style={{ flex: 1 }}>
          <label htmlFor="c-cidade">Cidade</label>
          <input id="c-cidade" value={cidade} onChange={(e) => setCidade(e.target.value)} />
        </div>
        <div style={{ width: 80 }}>
          <label htmlFor="c-estado">UF</label>
          <input id="c-estado" value={estado} maxLength={2} onChange={(e) => setEstado(e.target.value.toUpperCase())} />
        </div>
      </div>

      <label htmlFor="c-cnpj">CNPJ</label>
      <input id="c-cnpj" value={cnpj} onChange={(e) => setCnpj(e.target.value)} />

      <label htmlFor="c-email">E-mail de contato</label>
      <input id="c-email" type="email" value={emailContato} onChange={(e) => setEmailContato(e.target.value)} />

      <label htmlFor="c-telefone">Telefone de contato</label>
      <input id="c-telefone" value={telefoneContato} onChange={(e) => setTelefoneContato(e.target.value)} />

      <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
        <button className="botao botao-primario" style={{ marginTop: 0 }} type="submit" disabled={salvando}>
          {salvando ? "Salvando…" : "Salvar"}
        </button>
        <button className="botao botao-texto" type="button" onClick={aoFechar}>
          Cancelar
        </button>
      </div>
    </form>
  );
}
